import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import { fetchInfinite } from '@dashboard/services/request';
import getSubmissionStatusLabel from '@dashboard/services/status/getSubmissionStatusLabel';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Button from '@dashboard/components/Button';
import { Checkbox, FormElement, Label, Select } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import Lozenge from '@dashboard/components/Lozenge';
import Spinner from '@dashboard/components/Spinner';
import StatusBadge from '@dashboard/components/StatusBadge';

import Table from '../components/Table';

const Submissions = () => {
  const { token } = useAuth();
  const history = useHistory();

  const query = useUrlQuery();
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [livemode, setLivemode] = useState(true);
  const [status, setStatus] = useState(query.status || 'all');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        livemode,
        status,
      }),
    });
  }, [sortBy, sortDirection, livemode, status]);

  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['submissions', sortBy, sortDirection, livemode, status],
    queryFn: async ({ pageParam }) =>
      fetchInfinite(
        `/admin/submissions?${stringify({
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          ...(livemode !== undefined ? { livemode } : {}),
          ...(status !== 'all' ? { status } : {}),
          limit: 20,
          skip: pageParam * 20,
        })}`,
        token,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;

    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  return (
    <div className="pr-4">
      <div className="flex items-end justify-end gap-x-2">
        <FormElement className="!mb-0">
          <div className="flex items-center gap-x-2 w-full">
            <Checkbox
              id="livemode"
              value={!livemode}
              checked={!livemode}
              onChange={() => setLivemode(livemode ? undefined : true)}
            />

            <Label isInline htmlFor="livemode">
              Include test payments
            </Label>
          </div>
        </FormElement>

        <FormElement className="!mb-0">
          <Label>Status</Label>
          <Select value={status} onChange={e => setStatus(e.target.value)} className="w-32">
            <option value="all">All</option>
            <option value="succeeded">Paid</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </Select>
        </FormElement>
      </div>

      <Table.Table>
        <Table.Head>
          <Table.Th
            onSort={onSort}
            sortKey="page.title"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Form
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="seller.displayName"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Merchant
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="createdAt"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Date
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="status" sortBy={sortBy} sortDirection={sortDirection}>
            Status
          </Table.Th>
        </Table.Head>

        {!isLoading && (
          <BottomScrollListener onBottom={fetchNextPage} as="tbody">
            {data.pages.map(page =>
              page.data.map(submission => (
                <Table.Row key={submission.id}>
                  <Table.Td>
                    <Link href={`/pages/${submission.page._id}`}>
                      {submission.page.title || submission.page.name || 'Untitled'}
                    </Link>

                    {!submission.livemode && <StatusBadge status="test">Test</StatusBadge>}
                  </Table.Td>
                  <Table.Td className="relative">
                    <Link href={`/admin/merchants/${submission.seller?._id}`}>
                      {submission.seller?.displayName || submission.seller?.name}
                    </Link>

                    {submission.seller?.disabled && (
                      <Lozenge className="ml-2 absolute right-0 mt-0.5">Disabled</Lozenge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {format(new Date(submission.createdAt), 'd MMM, yyyy, HH:mm')}
                  </Table.Td>
                  <Table.Td className="space-x-2">
                    <StatusBadge
                      status={submission.status}
                      label={getSubmissionStatusLabel(submission.status)}
                    />

                    <Button
                      to={`${getCheckoutUrl(submission.page, submission.seller)}`}
                      appearance="light"
                      target="_blank"
                      size="small"
                    >
                      View form
                    </Button>
                  </Table.Td>
                </Table.Row>
              )),
            )}
          </BottomScrollListener>
        )}
      </Table.Table>

      {(isFetchingNextPage || isLoading) && <Spinner />}
    </div>
  );
};

export default Submissions;
